/* eslint-disable no-param-reassign  */

import { createSlice } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import SessionRepository from 'repositories/SessionRepository';
import OauthGoogleAuthenticationRepository from 'repositories/OauthGoogleAuthenticationRepository';
import RegistrationRepository from 'repositories/RegistrationRepository';
import ValidateTokenRepository from 'repositories/auth/ValidateTokenRepository';
import ProfileImageRepository from 'repositories/ProfileImageRepository';
import CompanyLogoRepository from 'repositories/CompanyLogoRepository';
import RealEstateAgentRepository from 'repositories/auth/RealEstateAgentRepository';
import LoanOfficerSettingsRepository from 'repositories/loanOfficer/LoanOfficerSettingsRepository';
import { FETCH_STATUSES } from 'utils/fetchStatusUtils';
import { getTokenHeadersFromLocalStorage, removeTokenHeadersFromLocalStorage } from 'hooks/useTokens';

const initialState = {
  updatingStatus: FETCH_STATUSES.idle,
  passwordUpdatingStatus: FETCH_STATUSES.idle,
  error: {},
  logOutStatus: FETCH_STATUSES.idle,
  loggingInStatus: FETCH_STATUSES.idle,
  currentUser: null,
  currentUserLoadingStatus: FETCH_STATUSES.idle,
  userRegistrationStatus: FETCH_STATUSES.idle,
  profileCompanyLogoSavingStatus: FETCH_STATUSES.idle,
  profileImageSavingStatus: FETCH_STATUSES.idle,
};

const appSlice = createSlice({
  name: 'appSlice',
  initialState,
  reducers: {
    loadCurrentUserStart(state) {
      state.currentUserLoadingStatus = FETCH_STATUSES.pending;
    },
    loadCurrentUserSuccess(state, { payload }) {
      const { currentUser } = payload;
      state.currentUser = currentUser.data;
      state.currentUserLoadingStatus = FETCH_STATUSES.fulfilled;
    },
    loadCurrentUserFail(state) {
      state.currentUserLoadingStatus = FETCH_STATUSES.failed;
    },
    signUpStart(state) {
      state.userRegistrationStatus = FETCH_STATUSES.pending;
      state.error = {};
    },
    signUpSuccess(state) {
      state.userRegistrationStatus = FETCH_STATUSES.fulfilled;
    },
    signUpFailed(state, { payload }) {
      const { error } = payload;
      state.userRegistrationStatus = FETCH_STATUSES.failed;
      state.error = error;
    },
    logoutStart(state) {
      state.logOutStatus = FETCH_STATUSES.pending;
    },
    logoutSuccess(state) {
      state.currentUser = null;
      state.logOutStatus = FETCH_STATUSES.fulfilled;
    },
    logoutFail(state) {
      state.logOutStatus = FETCH_STATUSES.failed;
    },
    loginStart(state) {
      state.loggingInStatus = FETCH_STATUSES.pending;
    },
    loginSuccess(state) {
      state.loggingInStatus = FETCH_STATUSES.fulfilled;
    },
    loginFail(state) {
      state.loggingInStatus = FETCH_STATUSES.failed;
    },
    loginWithSocialStart(state) {
      state.loggingInStatus = FETCH_STATUSES.pending;
    },
    loginWithSocialSuccess(state) {
      state.loggingInStatus = FETCH_STATUSES.fulfilled;
    },
    loginWithSocialFail(state) {
      state.loggingInStatus = FETCH_STATUSES.failed;
    },
    updatePasswordStart(state) {
      state.passwordUpdatingStatus = FETCH_STATUSES.pending;
    },
    updatePasswordSuccess(state) {
      state.passwordUpdatingStatus = FETCH_STATUSES.fulfilled;
    },
    updatePasswordFail(state) {
      state.passwordUpdatingStatus = FETCH_STATUSES.failed;
    },
    updateBuyerStart(state) {
      state.updatingStatus = FETCH_STATUSES.pending;
    },
    updateBuyerSuccess(state, { payload }) {
      const { buyer } = payload;

      state.updatingStatus = FETCH_STATUSES.fulfilled;
      state.currentUser = { ...state.currentUser, ...buyer };
    },
    updateBuyerFail(state) {
      state.updatingStatus = FETCH_STATUSES.failed;
    },
    updateLoanOfficerStart(state) {
      state.updatingStatus = FETCH_STATUSES.pending;
    },
    updateLoanOfficerSuccess(state, { payload }) {
      const { loanOfficer } = payload;
      state.currentUser = { ...state.currentUser, ...loanOfficer };
      state.updatingStatus = FETCH_STATUSES.fulfilled;
    },
    updateLoanOfficerFail(state) {
      state.updatingStatus = FETCH_STATUSES.failed;
    },
    updateRealEstateAgentStart(state) {
      state.updatingStatus = FETCH_STATUSES.pending;
    },
    updateRealEstateAgentSuccess(state, { payload }) {
      const { realEstateAgent } = payload;
      state.currentUser = { ...state.currentUser, ...realEstateAgent };
      state.updatingStatus = FETCH_STATUSES.fulfilled;
    },
    updateRealEstateAgentFail(state) {
      state.updatingStatus = FETCH_STATUSES.failed;
    },
    saveProfileImageStart(state) {
      state.profileImageSavingStatus = FETCH_STATUSES.pending;
    },
    saveProfileImageSuccess(state, { payload }) {
      const { profileImageUrl } = payload;
      state.currentUser = { ...state.currentUser, profileImageUrl };
      state.profileImageSavingStatus = FETCH_STATUSES.fulfilled;
    },
    saveProfileImageFail(state) {
      state.profileImageSavingStatus = FETCH_STATUSES.failed;
    },
    saveCompanyLogoStart(state) {
      state.profileCompanyLogoSavingStatus = FETCH_STATUSES.pending;
    },
    saveCompanyLogoSuccess(state, { payload }) {
      const { companyLogoUrl } = payload;
      state.currentUser = { ...state.currentUser, companyLogoUrl };
      state.profileCompanyLogoSavingStatus = FETCH_STATUSES.fulfilled;
    },
    saveCompanyLogoFail(state) {
      state.profileCompanyLogoSavingStatus = FETCH_STATUSES.failed;
    },
    saveProfileVideoSuccess(state, { payload }) {
      const { profileVideoUrl } = payload;
      state.currentUser = { ...state.currentUser, profileVideoUrl };
    },
  },
});

export const { actions } = appSlice;

export default appSlice.reducer;

export const useAppActions = () => {
  const dispatch = useDispatch();

  const logout = async () => {
    dispatch(actions.logoutStart());
    try {
      await SessionRepository.destroy();
      removeTokenHeadersFromLocalStorage();

      dispatch(actions.logoutSuccess());
      return Promise.resolve();
    } catch (_error) {
      dispatch(actions.logoutFail());
      return Promise.reject();
    }
  };

  const login = async attrs => {
    dispatch(actions.loginStart());
    try {
      await SessionRepository.create(attrs);
      dispatch(actions.loginSuccess());
    } catch (error) {
      dispatch(actions.loginFail());
      throw error;
    }
  };

  const loginWithGoogle = async attrs => {
    dispatch(actions.loginWithSocialStart());
    try {
      await OauthGoogleAuthenticationRepository.create(attrs);
      dispatch(actions.loginWithSocialSuccess());
    } catch (error) {
      dispatch(actions.loginWithSocialFail());
      throw error;
    }
  };

  const signUp = async attrs => {
    dispatch(actions.signUpStart());
    try {
      await RegistrationRepository.create(attrs);
      dispatch(actions.signUpSuccess());
      return Promise.resolve();
    } catch (error) {
      const { data } = error.response;
      dispatch(actions.signUpFailed({ error: data }));
      throw error;
    }
  };

  const updatePassword = async attrs => {
    dispatch(actions.updatePasswordStart());
    try {
      await SessionRepository.updatePassword(attrs);
      dispatch(actions.updatePasswordSuccess());
    } catch (error) {
      dispatch(actions.updatePasswordFail());
      throw error;
    }
  };

  const updateBuyer = async attrs => {
    dispatch(actions.updateBuyerStart());
    try {
      const response = await SessionRepository.updateBuyer(attrs);
      dispatch(actions.updateBuyerSuccess({ buyer: response }));
    } catch (error) {
      dispatch(actions.updateBuyerFail());
      throw error;
    }
  };

  const loadCurrentUser = async () => {
    dispatch(actions.loadCurrentUserStart());
    try {
      const params = getTokenHeadersFromLocalStorage();
      const currentUser = await ValidateTokenRepository.get(params);
      dispatch(actions.loadCurrentUserSuccess({ currentUser }));
    } catch (_error) {
      dispatch(actions.loadCurrentUserFail());
    }
  };

  const updateLoanOfficer = async attr => {
    dispatch(actions.updateLoanOfficerStart());
    try {
      const res = await LoanOfficerSettingsRepository.update(attr);
      dispatch(actions.updateLoanOfficerSuccess({ loanOfficer: res }));
    } catch (error) {
      dispatch(actions.updateLoanOfficerFail());
      throw error;
    }
  };

  const updateRealEstateAgent = async attr => {
    dispatch(actions.updateRealEstateAgentStart());
    try {
      const res = await RealEstateAgentRepository.update(attr);
      dispatch(actions.updateRealEstateAgentSuccess({ realEstateAgent: res }));
    } catch (error) {
      dispatch(actions.updateRealEstateAgentFail());
      throw error;
    }
  };

  const saveProfileImage = async attrs => {
    dispatch(actions.saveProfileImageStart());
    try {
      const { profileImageUrl } = await ProfileImageRepository.create(attrs);
      dispatch(actions.saveProfileImageSuccess({ profileImageUrl }));
      return Promise.resolve();
    } catch (error) {
      dispatch(actions.saveProfileImageFail());
      return Promise.reject();
    }
  };

  const saveCompanyLogo = async attrs => {
    dispatch(actions.saveCompanyLogoStart());
    try {
      const { companyLogoImageUrl } = await CompanyLogoRepository.create(attrs);

      dispatch(actions.saveCompanyLogoSuccess({ companyLogoUrl: companyLogoImageUrl }));
      return Promise.resolve();
    } catch (error) {
      dispatch(actions.saveCompanyLogoFail());
      return Promise.reject();
    }
  };

  const saveProfileVideo = async attrs => {
    try {
      const { profileVideoUrl } = await LoanOfficerSettingsRepository.saveProfileVideo(attrs);
      dispatch(actions.saveProfileVideoSuccess({ profileVideoUrl }));
      return Promise.resolve();
    } catch (error) {
      return Promise.reject();
    }
  };

  return {
    signUp,
    login,
    loadCurrentUser,
    updateLoanOfficer,
    saveProfileImage,
    saveCompanyLogo,
    saveProfileVideo,
    logout,
    updatePassword,
    updateRealEstateAgent,
    updateBuyer,
    loginWithGoogle,
  };
};
/* eslint-enable no-param-reassign */



// WEBPACK FOOTER //
// ./src/reducers/slices/appSlice.js